/**
 * Price versions of one product (owner decision A, 2026-09-25): the product
 * and its versions, each version naming the product by its stable key
 * (`versionOf`). The one reader every grouping of versions calls, in Nest,
 * the builder and here; Python's `price_versions.py` reads the same link
 * (`tests/fixtures/questionnaire-builder/price-versions.golden.json`). A name
 * is never read for it: a stored draft without the link reads as separate
 * products.
 */

function cleanText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/** An asset as the price-version reader reads it: its family and keys. */
interface VersionedAsset {
  type?: unknown;
  stableKey?: unknown;
  versionOf?: unknown;
}

/**
 * The product a product asset is, or is a price version of, by its stable
 * key: its `versionOf`, else its own key (owner decision A, 2026-09-25).
 * Undefined for any other asset. The one typed link every reader groups
 * versions by; a name is never read for it (Python `price_versions.py`).
 */
export function priceVersionProductKey(asset: VersionedAsset): string | undefined {
  if (asset.type !== "product") return undefined;
  return cleanText(asset.versionOf) || cleanText(asset.stableKey) || undefined;
}

/**
 * Whether these assets are price versions of one product: two or more
 * products that name one product (`priceVersionProductKey`). They share its
 * name, and an `@mention` of it names the product.
 */
export function arePriceVersionsOfOneProduct(
  assets: ReadonlyArray<VersionedAsset>,
): boolean {
  const products = new Set(assets.map(priceVersionProductKey));
  return assets.length > 1 && products.size === 1 && !products.has(undefined);
}

/**
 * The price versions of the product `asset` is or versions, in catalog
 * order and `asset` among them; empty when it is no price version.
 */
export function priceVersionsOf<T extends VersionedAsset>(
  asset: VersionedAsset,
  assets: ReadonlyArray<T>,
): Array<T> {
  const product = priceVersionProductKey(asset);
  if (product === undefined) return [];
  const versions = assets.filter(
    (candidate) => priceVersionProductKey(candidate) === product,
  );
  return versions.length > 1 ? versions : [];
}
